import { useQuery } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { getCountryName } from "common/helpers/utils";
import type { CustomProxy } from "common/types";

async function getProxies(workspaceId: string): Promise<CustomProxy[]> {
  const { proxies } = await get(`workspaces/${workspaceId}/custom_proxies`);

  // add country name into proxy.location
  return proxies.map(
    (
      proxy: Omit<CustomProxy, "location"> & {
        location: Omit<CustomProxy["location"], "country">;
      },
    ) => ({
      ...proxy,
      location: proxy.location
        ? {
            ...proxy.location,
            country: getCountryName(proxy.location.countryISO),
          }
        : undefined,
    }),
  );
}

export default function useWorkspaceProxies() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { proxiesKeys } = getQueryKeys(workspaceId);

  const {
    data: workspaceProxies,
    isLoading: isLoadingWorkspaceProxies,
    isError: isWorkspaceProxiesError,
    refetch: refetchWorkspaceProxies,
  } = useQuery({
    queryKey: proxiesKeys.list,
    queryFn: () => getProxies(workspaceId),
  });

  return {
    workspaceProxies,
    isLoadingWorkspaceProxies,
    isWorkspaceProxiesError,
    refetchWorkspaceProxies,
  };
}
